import { Fragment, useCallback, useEffect, useState } from 'react';
import type {
  AuthMe,
  CalendarAvailableRoom,
  CalendarEvent,
  CalendarIntegration,
  ChatAvailableSpace,
  ChatIntegration,
  ChatRecentMessage,
  NoticeDraftSet,
} from '@cornerstone/shared';
import { GOOGLE_SCOPE } from '@cornerstone/shared';
import { API_BASE } from '../client/config';
import { ConfirmModal, ModalShell, TextPromptModal } from '../components/menu';
import { ContentSkeleton } from '../components/skeleton/ContentSkeleton';
import { describeApiError, toast } from '../components/toast';
import { Button } from '../components/ui';
import { PageTitle } from '../nav/PageTitle';

// =============================================================================
// "폴링 결과" preview modal — opened after a successful manual poll on
// any Chat or Calendar row. We feed it a discriminated union so a single
// component renders both the Chat (message list) and Calendar (event list)
// shapes without prop drilling explosion.
// =============================================================================

type PollPreview =
  | {
      kind: 'chat';
      /** Used by the reparse button to address the row. */
      integrationId: string;
      label: string;
      pollingState: 'polling' | 'loaded' | 'error' | 'reparsing';
      messages: ChatRecentMessage[];
      errorMessage?: string;
      /** Banner under the header summarising the most recent reparse call. */
      reparseStats?: {
        total: number;
        succeeded: number;
        failed: number;
        hydrated?: number;
        skippedMissingBody?: number;
      } | null;
    }
  | {
      kind: 'calendar';
      label: string;
      pollingState: 'polling' | 'loaded' | 'error';
      events: CalendarEvent[];
      rangeFromIso: string;
      rangeToIso: string;
      errorMessage?: string;
    };

// =============================================================================
// 확인·입력 모달 상태 (검수 S19).
//
// 이 페이지는 원래 window.confirm/prompt/alert로 되묻고 알렸다. 네이티브 대화상자는
// 위치·크기·버튼 문구가 브라우저 몫이라 "조작부는 움직이지 않는다"는 앱 원칙을
// 지킬 수 없고, 다크 테마·한국어 문구도 따로 논다. 전부 앱 모달(ConfirmModal /
// TextPromptModal / CsvPromptModal)과 토스트로 옮겼다.
//
// "무엇에 대해 무엇을 묻는 중인지"를 하나의 union state에 담는다.
//   • 모달이 동시에 두 개 뜨는 사고가 구조적으로 불가능해지고,
//   • 취소는 전부 setDialog(null) 하나로 끝난다(= 기존 prompt 취소 = 무동작).
// =============================================================================
type IntegrationsDialog =
  | { kind: 'approvalSpaces'; integration: ChatIntegration }
  | { kind: 'operators'; integration: ChatIntegration }
  | { kind: 'sendApproval'; draft: NoticeDraftSet; initialSpaceIds: string }
  | { kind: 'deleteDraft'; draft: NoticeDraftSet; title: string }
  | { kind: 'callbackProbe'; integration: ChatIntegration; suggestedSpaceId: string }
  // 전체 리셋은 되돌릴 수 없어 2단계다: 범위를 밝힌 확인 → "RESET" 타이핑.
  | { kind: 'resetWarn' }
  | { kind: 'resetType' }
  | { kind: 'removeChat'; integration: ChatIntegration }
  | { kind: 'removeCalendar'; integration: CalendarIntegration };

// =============================================================================
// Integrations — Phase 6 / 6.6 Admin surface.
//
// Sections:
//   1. Google 권한 — show grantedScopes, offer "scope 추가" links
//   2. Chat 채팅방 추가 / 등록 목록 (Phase 6 — auto-Notice from [공지] msgs)
//   3. Calendar 회의실 추가 / 등록 목록 (Phase 6.6 — meeting-room status data
//      cached every 60 s; Player template renders separately)
// =============================================================================

type MeState =
  | { kind: 'loading' }
  | { kind: 'unauthenticated'; reason: string }
  | { kind: 'authenticated'; me: AuthMe };

export function Integrations(): JSX.Element {
  const [meState, setMeState] = useState<MeState>({ kind: 'loading' });
  const [initialLoading, setInitialLoading] = useState(true);
  // H9 IA(HNBL-XXX): 공지 목록은 별도 페이지(/h9/notices)로 분리됨. 이 페이지는
  // 연동 설정(권한·Chat·Calendar)만 담당한다.
  const [integrations, setIntegrations] = useState<ChatIntegration[]>([]);
  const [pendingDrafts, setPendingDrafts] = useState<NoticeDraftSet[]>([]);
  const [availableSpaces, setAvailableSpaces] = useState<ChatAvailableSpace[]>([]);
  const [discoveryErr, setDiscoveryErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draftSpaceId, setDraftSpaceId] = useState('');
  const [draftLabel, setDraftLabel] = useState('');
  const [probePostingId, setProbePostingId] = useState<string | null>(null);
  // 공지 키워드(filterPattern) 인라인 편집 상태
  const [keywordEditId, setKeywordEditId] = useState<string | null>(null);
  const [keywordMode, setKeywordMode] = useState<'chips' | 'raw'>('chips');
  const [keywordDraft, setKeywordDraft] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [rawPattern, setRawPattern] = useState('');

  // Calendar (Phase 6.6)
  const [calendars, setCalendars] = useState<CalendarIntegration[]>([]);
  const [availableRooms, setAvailableRooms] = useState<CalendarAvailableRoom[]>(
    [],
  );
  const [roomDiscoveryErr, setRoomDiscoveryErr] = useState<string | null>(null);
  const [draftCalendarId, setDraftCalendarId] = useState('');
  const [draftCalendarLabel, setDraftCalendarLabel] = useState('');

  // Poll-result preview modal (shared across Chat + Calendar rows).
  const [preview, setPreview] = useState<PollPreview | null>(null);

  // 확인·입력 모달 (S19). 한 번에 하나만 열린다.
  const [dialog, setDialog] = useState<IntegrationsDialog | null>(null);
  // Callback Probe 발송 뒤 안내는 "다음에 할 일" 절차라 한 줄 토스트로는 담기지
  // 않는다. 확인 버튼만 있는 알림 모달로 dialog와 별도로 띄운다.
  const [probeNotice, setProbeNotice] = useState<string | null>(null);

  const loadAll = useCallback(async (signal?: AbortSignal) => {
    const init: RequestInit = signal ? { signal } : {};
    try {
      const meRes = await fetch(`${API_BASE}/api/auth/me`, init);
      if (meRes.ok) {
        setMeState({ kind: 'authenticated', me: (await meRes.json()) as AuthMe });
      } else if (meRes.status === 401) {
        setMeState({
          kind: 'unauthenticated',
          reason:
            'Admin 로그인이 필요합니다. AUTH_ENABLED=true로 설정 후 dev 서버를 재시작했는지 확인해 주세요.',
        });
        return;
      } else {
        const text = await meRes.text().catch(() => '');
        setMeState({
          kind: 'unauthenticated',
          reason: `/api/auth/me → HTTP ${meRes.status} ${text}`,
        });
        return;
      }
      const listRes = await fetch(`${API_BASE}/api/integrations/chat`, init);
      if (listRes.ok) {
        setIntegrations((await listRes.json()) as ChatIntegration[]);
      } else if (listRes.status === 403) {
        setErr('관리자 권한이 필요합니다.');
      }
      const calRes = await fetch(`${API_BASE}/api/integrations/calendar`, init);
      if (calRes.ok) {
        setCalendars((await calRes.json()) as CalendarIntegration[]);
      }
      const draftRes = await fetch(
        `${API_BASE}/api/integrations/chat/draft-sets?status=needs_approval_space`,
        init,
      );
      if (draftRes.ok) {
        setPendingDrafts((await draftRes.json()) as NoticeDraftSet[]);
      }
    } catch (e) {
      if (signal?.aborted) return;
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      if (!signal?.aborted) setInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void loadAll(ac.signal);
    const t = setInterval(() => void loadAll(ac.signal), 15_000);
    return () => {
      clearInterval(t);
      ac.abort();
    };
  }, [loadAll]);

  const me = meState.kind === 'authenticated' ? meState.me : null;

  const hasChatScope = me?.grantedScopes.includes(GOOGLE_SCOPE.chatReadonly);
  const hasSpacesScope = me?.grantedScopes.includes(
    GOOGLE_SCOPE.chatSpacesReadonly,
  );
  // The umbrella calendar.readonly implies both calendarlist + events. We
  // accept any of the three so an operator who chose the narrower scope
  // upfront still works.
  const hasCalendarScope =
    me?.grantedScopes.includes(GOOGLE_SCOPE.calendarReadonly) ||
    me?.grantedScopes.includes(GOOGLE_SCOPE.calendarEventsReadonly) ||
    me?.grantedScopes.includes(GOOGLE_SCOPE.calendarListReadonly);

  // Auto-load space list when both scopes are present.
  useEffect(() => {
    if (!hasSpacesScope) return;
    const ac = new AbortController();
    fetch(`${API_BASE}/api/integrations/chat/available-spaces`, { signal: ac.signal })
      .then(async (r) => {
        if (r.ok) {
          const list = (await r.json()) as ChatAvailableSpace[];
          setAvailableSpaces(list.filter((s) => s.spaceType === 'SPACE'));
          setDiscoveryErr(null);
        } else {
          const detail = await r.text().catch(() => '');
          setDiscoveryErr(`${r.status}: ${detail.slice(0, 200)}`);
        }
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setDiscoveryErr(e instanceof Error ? e.message : String(e));
      });
    return () => ac.abort();
  }, [hasSpacesScope]);

  // Auto-load calendar room list when the calendar scope is present.
  useEffect(() => {
    if (!hasCalendarScope) return;
    const ac = new AbortController();
    fetch(`${API_BASE}/api/integrations/calendar/available-rooms`, { signal: ac.signal })
      .then(async (r) => {
        if (r.ok) {
          const list = (await r.json()) as CalendarAvailableRoom[];
          setAvailableRooms(list);
          setRoomDiscoveryErr(null);
        } else {
          const detail = await r.text().catch(() => '');
          setRoomDiscoveryErr(`${r.status}: ${detail.slice(0, 200)}`);
        }
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setRoomDiscoveryErr(e instanceof Error ? e.message : String(e));
      });
    return () => ac.abort();
  }, [hasCalendarScope]);

  async function addIntegration(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    if (!me?.email) return;
    // Discovery returns a resource name (`spaces/{id}`), while the backend
    // stores the canonical bare id. Manual input accepts either form.
    const spaceId = draftSpaceId.trim().replace(/^(?:spaces\/)+/, '');
    const label = draftLabel.trim();
    if (!spaceId || !label) {
      setErr('Space ID와 라벨을 모두 입력하세요.');
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/api/integrations/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spaceId, label, ownerEmail: me.email }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`${r.status}: ${t}`);
      }
      setDraftSpaceId('');
      setDraftLabel('');
      void loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggle(integration: ChatIntegration): Promise<void> {
    await fetch(`${API_BASE}/api/integrations/chat/${integration.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !integration.enabled }),
    });
    void loadAll();
  }

  async function updateChatConfig(
    integration: ChatIntegration,
    patch: Partial<Pick<ChatIntegration, 'approvalSpaceIds' | 'operatorEmails' | 'autoBroadcast' | 'triggerMode' | 'filterPattern'>>,
  ): Promise<void> {
    await fetch(`${API_BASE}/api/integrations/chat/${integration.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });
    void loadAll();
  }


  function openKeywordEditor(integration: ChatIntegration): void {
    const kws = patternToKeywords(integration.filterPattern);
    setKeywordEditId(integration.id);
    setKeywordInput('');
    if (kws) {
      setKeywordMode('chips');
      setKeywordDraft(kws);
      setRawPattern('');
    } else {
      // 비표준(커스텀) 정규식 → 원시 편집으로 폴백
      setKeywordMode('raw');
      setKeywordDraft([]);
      setRawPattern(integration.filterPattern);
    }
  }

  function addKeyword(): void {
    const kw = keywordInput.trim();
    if (!kw) return;
    if (/[[\]|]/.test(kw)) {
      setErr('키워드에 [ ] | 문자는 쓸 수 없습니다.');
      return;
    }
    if (keywordDraft.includes(kw)) {
      setKeywordInput('');
      return;
    }
    setKeywordDraft([...keywordDraft, kw]);
    setKeywordInput('');
  }

  async function saveKeywords(integration: ChatIntegration): Promise<void> {
    let pattern: string;
    if (keywordMode === 'raw') {
      pattern = rawPattern.trim();
      if (!pattern) {
        setErr('정규식이 비어 있습니다.');
        return;
      }
    } else {
      const kws = keywordDraft.map((k) => k.trim()).filter((k) => k.length > 0);
      if (kws.length === 0) {
        setErr('공지 키워드를 1개 이상 입력하세요.');
        return;
      }
      pattern = keywordsToPattern(kws);
    }
    await updateChatConfig(integration, { filterPattern: pattern });
    setKeywordEditId(null);
  }

  /** 승인요청 모달을 연다 — 기본값은 해당 연동에 설정된 승인방. */
  function openSendApproval(draft: NoticeDraftSet): void {
    const integration = integrations.find((it) => it.id === draft.integrationId);
    setDialog({
      kind: 'sendApproval',
      draft,
      initialSpaceIds: (integration?.approvalSpaceIds ?? []).join(', '),
    });
  }

  async function sendApprovalRequest(draft: NoticeDraftSet, next: string): Promise<void> {
    const approvalSpaceIds = splitCsv(next);
    if (approvalSpaceIds.length === 0) {
      // 모달 안에 이유를 띄우려고 던진다. source space로는 자동 발송하지 않는다.
      throw new Error('승인방을 1개 이상 입력해야 합니다. source space로는 자동 발송하지 않습니다.');
    }
    const res = await fetch(
      `${API_BASE}/api/integrations/chat/draft-sets/${draft.id}/send-approval`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approvalSpaceIds }),
      },
    );
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      // 모달이 err 배너를 가리므로 실패는 모달 안에서 보여준다.
      throw new Error(`승인요청 실패: ${res.status} ${detail}`);
    }
    void loadAll();
  }

  async function deletePendingDraft(draft: NoticeDraftSet): Promise<void> {
    setErr(null);
    const res = await fetch(`${API_BASE}/api/integrations/chat/draft-sets/${draft.id}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`대기 항목 삭제 실패: ${res.status} ${detail}`);
    }
    setPendingDrafts((prev) => prev.filter((item) => item.id !== draft.id));
    void loadAll();
  }

  async function sendCallbackProbe(
    integration: ChatIntegration,
    trimmed: string,
  ): Promise<void> {
    setErr(null);
    setProbePostingId(integration.id);
    try {
      const res = await fetch(`${API_BASE}/api/integrations/chat/callback-probe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spaceId: trimmed }),
      });
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(`${res.status}: ${detail}`);
      }
      const data = (await res.json()) as { messageName?: string };
      setProbeNotice(
        `Callback Probe 카드를 보냈습니다.\n\n1) Chat에서 "Callback Probe" 버튼을 클릭하세요.\n2) Cloudflare/서버 로그에서 /api/chat-app/events 도달 여부를 확인하세요.\n\nmessageName: ${data.messageName ?? '(unknown)'}`,
      );
    } finally {
      // 실패는 모달 안에 뜨도록 그대로 올려보낸다(기존 err 배너 대신).
      setProbePostingId(null);
    }
  }

  async function pollNow(integration: ChatIntegration): Promise<void> {
    setErr(null);
    // Open the modal in "polling" state immediately so the operator gets
    // instant feedback while the network round-trip is in flight.
    setPreview({
      kind: 'chat',
      integrationId: integration.id,
      label: integration.label,
      pollingState: 'polling',
      messages: [],
    });
    try {
      const r = await fetch(
        `${API_BASE}/api/integrations/chat/${integration.id}/test`,
        { method: 'POST' },
      );
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`${r.status}: ${t}`);
      }
      // Poll succeeded — fetch the newest ingested messages to display.
      const recentRes = await fetch(
        `${API_BASE}/api/integrations/chat/${integration.id}/recent?limit=20`,
      );
      const messages = recentRes.ok
        ? ((await recentRes.json()) as ChatRecentMessage[])
        : [];
      setPreview({
        kind: 'chat',
        integrationId: integration.id,
        label: integration.label,
        pollingState: 'loaded',
        messages,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setErr(message);
      setPreview({
        kind: 'chat',
        integrationId: integration.id,
        label: integration.label,
        pollingState: 'error',
        messages: [],
        errorMessage: message,
      });
    }
    void loadAll();
  }

  /** Phase 6.8 — destructive reset. Wipes every chat_ingested_messages row,
   *  resets per-integration poller counters, and deletes legacy auto-created
   *  `[Chat] …` Notice rows. Operator must confirm twice (mistype-proof).
   *  2단계 확인은 그대로 유지하되(resetWarn → resetType) 네이티브 대화상자 대신
   *  ConfirmModal → TextPromptModal로 잇는다. */
  async function resetIngestedAll(typed: string): Promise<void> {
    if (typed !== 'RESET') {
      // 오타는 모달을 닫지 않고 그 자리에서 알린다 — 다시 처음부터 묻지 않는다.
      throw new Error('확인 문구가 다릅니다. 대문자로 RESET 을 정확히 입력해 주세요.');
    }
    setErr(null);
    try {
      const r = await fetch(`${API_BASE}/api/integrations/chat/reset`, {
        method: 'POST',
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`${r.status}: ${t}`);
      }
      const result = (await r.json()) as {
        messages: number;
        notices: number;
      };
      toast(
        `리셋 완료. 메시지 ${result.messages}건, 공지 ${result.notices}건 삭제됨.`,
        'success',
      );
    } finally {
      // 실패는 모달 안에 원인이 뜨도록 올려보낸다.
      void loadAll();
    }
  }

  /** Phase 6.7 — call POST /chat/:id/reparse, then refresh the recent list
   *  so newly-parsed cards appear immediately. Long-running (~3 s × limit)
   *  so we flip the modal into a dedicated 'reparsing' state to discourage
   *  the operator from spamming the button. */
  async function reparseNow(
    integrationId: string,
    label: string,
    onlyMissing: boolean,
  ): Promise<void> {
    setErr(null);
    setPreview((prev) => {
      if (!prev || prev.kind !== 'chat') return prev;
      return { ...prev, pollingState: 'reparsing', reparseStats: null };
    });
    try {
      const url = `${API_BASE}/api/integrations/chat/${integrationId}/reparse?limit=10&onlyMissing=${onlyMissing}`;
      const r = await fetch(url, { method: 'POST' });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`${r.status}: ${t}`);
      }
      const stats = (await r.json()) as {
        total: number;
        succeeded: number;
        failed: number;
        hydrated?: number;
        skippedMissingBody?: number;
      };
      // Refresh the recent list so the new parsed fields show up.
      const recentRes = await fetch(
        `${API_BASE}/api/integrations/chat/${integrationId}/recent?limit=20`,
      );
      const messages = recentRes.ok
        ? ((await recentRes.json()) as ChatRecentMessage[])
        : [];
      setPreview({
        kind: 'chat',
        integrationId,
        label,
        pollingState: 'loaded',
        messages,
        reparseStats: stats,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setErr(message);
      setPreview((prev) => {
        if (!prev || prev.kind !== 'chat') return prev;
        return {
          ...prev,
          pollingState: 'error',
          errorMessage: message,
        };
      });
    }
  }

  async function remove(integration: ChatIntegration): Promise<void> {
    const r = await fetch(`${API_BASE}/api/integrations/chat/${integration.id}`, {
      method: 'DELETE',
    });
    if (!r.ok) throw new Error(`${r.status}: ${await r.text().catch(() => '')}`);
    void loadAll();
  }

  // --- Calendar handlers ---------------------------------------------------

  async function addCalendar(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    if (!me?.email) return;
    const calendarId = draftCalendarId.trim();
    const label = draftCalendarLabel.trim();
    if (!calendarId || !label) {
      setErr('회의실 캘린더와 라벨을 모두 선택/입력하세요.');
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/api/integrations/calendar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ calendarId, label, ownerEmail: me.email }),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`${r.status}: ${t}`);
      }
      setDraftCalendarId('');
      setDraftCalendarLabel('');
      void loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggleCalendar(integration: CalendarIntegration): Promise<void> {
    await fetch(`${API_BASE}/api/integrations/calendar/${integration.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !integration.enabled }),
    });
    void loadAll();
  }

  async function pollCalendarNow(
    integration: CalendarIntegration,
  ): Promise<void> {
    setErr(null);
    // Mirror the Chat preview UX — instant modal, then populate.
    const now = Date.now();
    const WINDOW_MS = 12 * 60 * 60 * 1000; // match the poller's ±12 h window
    const rangeFromIso = new Date(now - WINDOW_MS).toISOString();
    const rangeToIso = new Date(now + WINDOW_MS).toISOString();
    setPreview({
      kind: 'calendar',
      label: integration.label,
      pollingState: 'polling',
      events: [],
      rangeFromIso,
      rangeToIso,
    });
    try {
      const r = await fetch(
        `${API_BASE}/api/integrations/calendar/${integration.id}/test`,
        { method: 'POST' },
      );
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`${r.status}: ${t}`);
      }
      const eventsRes = await fetch(
        `${API_BASE}/api/integrations/calendar/${integration.id}/events?from=${encodeURIComponent(rangeFromIso)}&to=${encodeURIComponent(rangeToIso)}`,
      );
      const events = eventsRes.ok
        ? ((await eventsRes.json()) as CalendarEvent[])
        : [];
      setPreview({
        kind: 'calendar',
        label: integration.label,
        pollingState: 'loaded',
        events,
        rangeFromIso,
        rangeToIso,
      });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setErr(message);
      setPreview({
        kind: 'calendar',
        label: integration.label,
        pollingState: 'error',
        events: [],
        rangeFromIso,
        rangeToIso,
        errorMessage: message,
      });
    }
    void loadAll();
  }

  async function removeCalendar(
    integration: CalendarIntegration,
  ): Promise<void> {
    const r = await fetch(`${API_BASE}/api/integrations/calendar/${integration.id}`, {
      method: 'DELETE',
    });
    if (!r.ok) throw new Error(`${r.status}: ${await r.text().catch(() => '')}`);
    void loadAll();
  }

  if (initialLoading) return <div className="h-full overflow-auto p-6">
    <PageTitle title="Integrations" />
    <ContentSkeleton label="연동 설정을 불러오는 중…" variant="form" />
  </div>;

  return (
    <div className="h-full overflow-auto p-6 text-text">
      <PageTitle title="Integrations" />
      <p style={{ color: 'var(--color-text-dim)', fontSize: 16, marginBottom: 29 }}>
        Google Chat의 <code style={codeStyle}>[공지]</code> /{' '}
        <code style={codeStyle}>[공지사항]</code> 메시지를 자동으로 Scene으로
        변환하고, Google Calendar의 회의실(Resource) 일정을 60초마다 캐시합니다.
      </p>

      {err && (
        <div style={errorStyle}>
          {err}
          <button onClick={() => setErr(null)} style={dismissButton}>
            dismiss
          </button>
        </div>
      )}

      <section style={section}>
        <h2 style={h2}>1. Google 권한</h2>
        {meState.kind === 'unauthenticated' && (
          <div>
            <p style={{ ...dim, marginBottom: 14 }}>{meState.reason}</p>
            <a
              href={`${API_BASE}/api/auth/google/start?return_to=${encodeURIComponent(
                window.location.origin + '/#/integrations',
              )}`}
              style={{ ...primaryButton, textDecoration: 'none' }}
            >
              Google 로그인
            </a>
          </div>
        )}
        {me && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ color: 'var(--color-green)' }}>
              로그인: {me.email} ({me.name ?? '이름 없음'})
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <ScopeBadge label="Chat 메시지" granted={!!hasChatScope} />
              <ScopeBadge label="Chat Space 목록" granted={!!hasSpacesScope} />
              <ScopeBadge label="Calendar" granted={!!hasCalendarScope} />
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              {!hasChatScope && (
                <a
                  href={`${API_BASE}/api/auth/google/extend?scope=${encodeURIComponent(
                    GOOGLE_SCOPE.chatReadonly,
                  )}&return_to=${encodeURIComponent(
                    window.location.origin + '/#/integrations',
                  )}`}
                  style={{ ...primaryButton, textDecoration: 'none' }}
                >
                  Chat 권한 추가
                </a>
              )}
              {!hasSpacesScope && (
                <a
                  href={`${API_BASE}/api/auth/google/extend?scope=${encodeURIComponent(
                    GOOGLE_SCOPE.chatSpacesReadonly,
                  )}&return_to=${encodeURIComponent(
                    window.location.origin + '/#/integrations',
                  )}`}
                  style={{ ...secondaryButton, textDecoration: 'none' }}
                >
                  Space 목록 권한 추가
                </a>
              )}
              {!hasCalendarScope && (
                <a
                  href={`${API_BASE}/api/auth/google/extend?scope=${encodeURIComponent(
                    GOOGLE_SCOPE.calendarReadonly,
                  )}&return_to=${encodeURIComponent(
                    window.location.origin + '/#/integrations',
                  )}`}
                  style={{ ...primaryButton, textDecoration: 'none' }}
                >
                  Calendar 권한 추가
                </a>
              )}
            </div>
          </div>
        )}
      </section>

      {me && hasChatScope && (
        <>
          <section style={section}>
            <h2 style={h2}>2. Chat 채팅방 추가</h2>
            {!hasSpacesScope && (
              <div style={{ marginBottom: 14 }}>
                <p style={dim}>
                  내가 속한 Chat 공간 목록을 자동으로 불러오려면 추가 권한이
                  필요합니다.
                </p>
              </div>
            )}
            {hasSpacesScope && discoveryErr && (
              <div style={errorStyle}>
                Space 목록 불러오기 실패: {discoveryErr}
              </div>
            )}
            <form
              onSubmit={addIntegration}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr auto',
                gap: 10,
                maxWidth: 1200,
                marginTop: 14,
              }}
            >
              {hasSpacesScope && availableSpaces.length > 0 ? (
                <select
                  value={draftSpaceId}
                  onChange={(e) => {
                    setDraftSpaceId(e.target.value);
                    const found = availableSpaces.find(
                      (s) => s.name.replace(/^spaces\//, '') === e.target.value,
                    );
                    if (found && !draftLabel) setDraftLabel(found.displayName);
                  }}
                  style={input}
                >
                  <option value="">
                    내 Space 목록에서 선택 ({availableSpaces.length}개)
                  </option>
                  {availableSpaces.map((s) => {
                    const id = s.name.replace(/^spaces\//, '');
                    return (
                      <option key={s.name} value={id}>
                        {s.displayName} — {id}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder="Space ID (예: AAAA1234XYZ)"
                  value={draftSpaceId}
                  onChange={(e) => setDraftSpaceId(e.target.value)}
                  style={input}
                />
              )}
              <input
                type="text"
                placeholder="라벨 (예: 본사 공지방)"
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                style={input}
              />
              <button type="submit" style={{ ...primaryButton, whiteSpace: 'nowrap' }}>
                추가
              </button>
            </form>
          </section>

          <section style={section}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 14,
                maxWidth: 1200,
                marginBottom: 10,
              }}
            >
              <h2 style={{ ...h2, margin: 0 }}>
                3. 등록된 채팅방 ({integrations.length})
              </h2>
              <button
                onClick={() => setDialog({ kind: 'resetWarn' })}
                style={{
                  ...secondaryButton,
                  borderColor: 'var(--color-red)',
                  color: 'var(--color-red)',
                  whiteSpace: 'nowrap',
                }}
                title="모든 ingested 메시지 + [Chat] 자동 생성 공지 삭제"
              >
                ingested 데이터 전체 리셋
              </button>
            </div>
            <div style={{ display: 'grid', gap: 10, maxWidth: 1200 }}>
              {integrations.map((it) => (
                <Fragment key={it.id}>
                <div style={row}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ fontWeight: 600 }}>{it.label}</div>
                    <div style={{ color: 'var(--color-text-dim)', fontSize: 13, fontFamily: 'monospace' }}>
                      {it.spaceId}
                    </div>
                  </div>
                  <div style={{ color: 'var(--color-text-dim)', fontSize: 13 }}>
                    <div>처리 {it.ingestedCount}건</div>
                    <div style={{ color: it.approvalSpaceIds.length === 0 ? 'var(--color-warning)' : 'var(--color-text-dim)' }}>
                      승인방 {it.approvalSpaceIds.length}개
                    </div>
                    <div>
                      {it.lastPolledAt
                        ? `폴링 ${relativeTime(it.lastPolledAt)}`
                        : '미폴링'}
                    </div>
                  </div>
                  <div style={{ color: it.lastError ? 'var(--color-red)' : 'var(--color-text-dim)', fontSize: 13 }}>
                    {it.lastError ?? '정상'}
                  </div>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => void pollNow(it)}
                      style={secondaryButton}
                      title="지금 폴링"
                    >
                      폴링
                    </button>
                    <button
                      onClick={() =>
                        setDialog({
                          kind: 'callbackProbe',
                          integration: it,
                          suggestedSpaceId: it.approvalSpaceIds[0] ?? it.spaceId,
                        })
                      }
                      disabled={probePostingId === it.id}
                      style={{
                        ...secondaryButton,
                        color: probePostingId === it.id ? 'var(--color-text-dim)' : 'var(--color-blue)',
                      }}
                      title="Google Chat callback 라우팅 진단 카드 발송"
                    >
                      {probePostingId === it.id ? '진단 발송 중...' : 'Callback 진단'}
                    </button>
                    <button
                      onClick={() => setDialog({ kind: 'approvalSpaces', integration: it })}
                      style={secondaryButton}
                    >
                      승인방 설정
                    </button>
                    <button
                      onClick={() => setDialog({ kind: 'operators', integration: it })}
                      style={secondaryButton}
                    >
                      운영자
                    </button>
                    <button
                      onClick={() => (keywordEditId === it.id ? setKeywordEditId(null) : openKeywordEditor(it))}
                      style={{
                        ...secondaryButton,
                        color: keywordEditId === it.id ? 'var(--color-accent)' : undefined,
                        borderColor: keywordEditId === it.id ? 'var(--color-accent)' : undefined,
                      }}
                      title="공지로 인식할 키워드 편집"
                    >
                      🏷 공지 키워드
                    </button>
                    {/* 방 모드 토글: 폴링 / 공지멘션 / 관리명령 */}
                    <button
                      onClick={() => void updateChatConfig(it, {
                        triggerMode: it.triggerMode === 'admin'
                          ? 'poll'
                          : it.triggerMode === 'mention'
                          ? 'admin'
                          : 'mention',
                      })}
                      style={{
                        ...secondaryButton,
                        color:
                          it.triggerMode === 'admin'
                            ? 'var(--color-accent)'
                            : it.triggerMode === 'mention'
                            ? 'var(--color-purple)'
                            : 'var(--color-text-dim)',
                        borderColor:
                          it.triggerMode === 'admin'
                            ? 'var(--color-accent)'
                            : it.triggerMode === 'mention'
                            ? 'var(--color-purple)'
                            : undefined,
                      }}
                      title={
                        it.triggerMode === 'admin'
                          ? '관리명령방 → 폴링방으로 변경'
                          : it.triggerMode === 'mention'
                          ? '멘션공지방 → 관리명령방으로 변경'
                          : '폴링방 → 멘션공지방으로 변경'
                      }
                    >
                      {it.triggerMode === 'admin'
                        ? '🛠 관리'
                        : it.triggerMode === 'mention'
                        ? '💬 멘션'
                        : '📡 폴링'}
                    </button>
                    <button
                      onClick={() => void toggle(it)}
                      style={{
                        ...secondaryButton,
                        color: it.enabled ? 'var(--color-warning)' : 'var(--color-green)',
                      }}
                    >
                      {it.enabled ? '비활성' : '활성'}
                    </button>
                    <button
                      onClick={() => setDialog({ kind: 'removeChat', integration: it })}
                      style={secondaryButton}
                    >
                      삭제
                    </button>
                  </div>
                </div>
                {keywordEditId === it.id && (
                  <div
                    style={{
                      border: '1px solid var(--color-accent)',
                      borderRadius: 8,
                      padding: 14,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      maxWidth: 1200,
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      공지 키워드 — <code style={{ fontFamily: 'monospace' }}>[키워드]</code> 로 시작하는 메시지를 공지로 인식
                    </div>
                    {keywordMode === 'chips' ? (
                      <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          {keywordDraft.length === 0 && <span style={dim}>키워드 없음</span>}
                          {keywordDraft.map((kw) => (
                            <span
                              key={kw}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: '3px 6px 3px 10px',
                                borderRadius: 14,
                                background: 'var(--color-bg-subtle, rgba(127,127,127,0.14))',
                                fontSize: 13,
                              }}
                            >
                              [{kw}]
                              <button
                                onClick={() => setKeywordDraft(keywordDraft.filter((k) => k !== kw))}
                                style={{ ...secondaryButton, padding: '0 6px', lineHeight: 1.4 }}
                                title="키워드 삭제"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: 6, maxWidth: 460 }}>
                          <input
                            value={keywordInput}
                            onChange={(e) => setKeywordInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                addKeyword();
                              }
                            }}
                            placeholder="키워드 입력 후 Enter (예: 안내)"
                            style={{
                              flex: 1,
                              padding: '6px 10px',
                              borderRadius: 6,
                              border: '1px solid var(--color-border, #ccc)',
                              background: 'transparent',
                              color: 'inherit',
                            }}
                          />
                          <button onClick={() => addKeyword()} style={secondaryButton}>
                            추가
                          </button>
                        </div>
                        <button
                          onClick={() => {
                            setRawPattern(keywordsToPattern(keywordDraft));
                            setKeywordMode('raw');
                          }}
                          style={{ ...secondaryButton, alignSelf: 'flex-start' }}
                        >
                          고급: 정규식 직접 편집
                        </button>
                      </>
                    ) : (
                      <>
                        <input
                          value={rawPattern}
                          onChange={(e) => setRawPattern(e.target.value)}
                          placeholder="^\\[(공지|공지사항)\\]\\s*"
                          style={{
                            width: '100%',
                            maxWidth: 460,
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: '1px solid var(--color-border, #ccc)',
                            background: 'transparent',
                            color: 'inherit',
                            fontFamily: 'monospace',
                          }}
                        />
                        <button
                          onClick={() => {
                            const k = patternToKeywords(rawPattern);
                            if (k) {
                              setKeywordDraft(k);
                              setKeywordMode('chips');
                            } else {
                              setErr('표준 형태(^\\[(...)\\]\\s*)가 아니라 키워드 칩으로 전환할 수 없습니다.');
                            }
                          }}
                          style={{ ...secondaryButton, alignSelf: 'flex-start' }}
                        >
                          키워드 편집으로 전환
                        </button>
                      </>
                    )}
                    <div style={{ display: 'flex', gap: 7 }}>
                      <button
                        onClick={() => void saveKeywords(it)}
                        style={{ ...secondaryButton, color: 'var(--color-green)', borderColor: 'var(--color-green)' }}
                      >
                        저장
                      </button>
                      <button onClick={() => setKeywordEditId(null)} style={secondaryButton}>
                        취소
                      </button>
                    </div>
                  </div>
                )}
                </Fragment>
              ))}
              {integrations.length === 0 && (
                <div style={dim}>아직 등록된 채팅방이 없습니다.</div>
              )}
            </div>
          </section>

          <section style={section}>
            <h2 style={h2}>4. 승인방 설정 필요 ({pendingDrafts.length})</h2>
            <p style={dim}>
              승인방이 없는 공지는 source space로 발송하지 않습니다. 승인방을 지정한 뒤 수동으로 승인요청을 보내세요.
            </p>
            <div style={{ display: 'grid', gap: 10, maxWidth: 1200, marginTop: 14 }}>
              {pendingDrafts.map((draft) => {
                const integration = integrations.find((it) => it.id === draft.integrationId);
                return (
                  <div key={draft.id} style={{ ...row, gridTemplateColumns: '2fr 1fr auto' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <div style={{ fontWeight: 600 }}>{draft.drafts[0]?.header ?? '공지 시안'}</div>
                      <div style={{ color: 'var(--color-text-dim)', fontSize: 13 }}>
                        {integration?.label ?? draft.integrationId} · {relativeTime(draft.createdAt)}
                      </div>
                    </div>
                    <div style={{ color: 'var(--color-warning)', fontSize: 14 }}>승인방 설정 필요</div>
                    <div style={actionGroup}>
                      {integration && (
                        <button
                          onClick={() => setDialog({ kind: 'approvalSpaces', integration })}
                          style={secondaryButton}
                        >
                          승인방 설정
                        </button>
                      )}
                      <button onClick={() => openSendApproval(draft)} style={primaryButton}>
                        승인요청 보내기
                      </button>
                      <button
                        onClick={() =>
                          setDialog({
                            kind: 'deleteDraft',
                            draft,
                            title: draft.drafts[0]?.header ?? '공지 시안',
                          })
                        }
                        style={{
                          ...secondaryButton,
                          borderColor: 'var(--color-red)',
                          color: 'var(--color-red)',
                        }}
                      >
                        삭제
                      </button>
                    </div>
                  </div>
                );
              })}
              {pendingDrafts.length === 0 && <div style={dim}>대기 중인 시안이 없습니다.</div>}
            </div>
          </section>
        </>
      )}

      {preview && (
        <PollPreviewModal
          preview={preview}
          onClose={() => setPreview(null)}
          onReparse={
            preview.kind === 'chat'
              ? (onlyMissing) =>
                  reparseNow(preview.integrationId, preview.label, onlyMissing)
              : undefined
          }
          onLocalMessageToggle={(messageId, excluded) => {
            setPreview((prev) => {
              if (!prev || prev.kind !== 'chat') return prev;
              return {
                ...prev,
                messages: prev.messages.map((m) =>
                  m.messageId === messageId ? { ...m, excluded } : m,
                ),
              };
            });
          }}
        />
      )}

      {/* ---- 확인·입력 모달 (S19: window.confirm/prompt/alert 대체) ---- */}
      {dialog?.kind === 'approvalSpaces' && (
        <CsvPromptModal
          title="승인방 설정"
          label="승인방 SPACE ID"
          hint={`'${dialog.integration.label}' 연동의 승인 요청을 받을 방입니다. 쉼표로 구분하고, 비워 두면 승인 요청이 발송되지 않습니다.`}
          initialValue={dialog.integration.approvalSpaceIds.join(', ')}
          placeholder="spaces/AAAA…, spaces/BBBB…"
          onClose={() => setDialog(null)}
          onSubmit={async (values) => {
            await updateChatConfig(dialog.integration, { approvalSpaceIds: values });
          }}
        />
      )}

      {dialog?.kind === 'operators' && (
        <CsvPromptModal
          title="운영자 설정"
          label="운영자 이메일"
          hint={`'${dialog.integration.label}' 연동의 공지를 승인할 수 있는 사람입니다. 쉼표로 구분합니다.`}
          initialValue={dialog.integration.operatorEmails.join(', ')}
          placeholder="hong@example.com, kim@example.com"
          onClose={() => setDialog(null)}
          onSubmit={async (values) => {
            await updateChatConfig(dialog.integration, { operatorEmails: values });
          }}
        />
      )}

      {dialog?.kind === 'sendApproval' && (
        <TextPromptModal
          title="승인요청 발송"
          label="승인방 SPACE ID (쉼표 구분)"
          initialValue={dialog.initialSpaceIds}
          placeholder="spaces/AAAA…"
          submitLabel="발송"
          onClose={() => setDialog(null)}
          onSubmit={async (value) => {
            await sendApprovalRequest(dialog.draft, value);
          }}
        />
      )}

      {dialog?.kind === 'deleteDraft' && (
        <ConfirmModal
          title="승인 대기 시안 삭제"
          message={`'${dialog.title}' 시안을 삭제합니다. 되돌릴 수 없지만, 같은 메시지가 다시 폴링되면 승인 대기로 돌아옵니다.`}
          confirmLabel="삭제"
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            await deletePendingDraft(dialog.draft);
          }}
        />
      )}

      {dialog?.kind === 'callbackProbe' && (
        <TextPromptModal
          title="Callback Probe 발송"
          label="보낼 SPACE ID"
          initialValue={dialog.suggestedSpaceId}
          placeholder="spaces/AAAA…"
          submitLabel="발송"
          onClose={() => setDialog(null)}
          onSubmit={async (value) => {
            await sendCallbackProbe(dialog.integration, value);
          }}
        />
      )}

      {dialog?.kind === 'resetWarn' && (
        <ConfirmModal
          title="ingested 메시지 전체 리셋"
          message={
            'ingested 메시지 전부와 [Chat] 자동 생성 공지 전부를 삭제하고, 연동별 폴러 카운터를 0으로 되돌립니다.\n' +
            '되돌릴 수 없습니다. 삭제된 메시지는 다시 폴링될 때까지 돌아오지 않습니다.'
          }
          confirmLabel="계속"
          // ConfirmModal은 onConfirm이 끝나면 곧바로 onCancel(=닫기)을 부른다.
          // 1단계에서 2단계로 넘어가는 중이면 그 닫기를 무시해야 모달이 이어진다.
          onCancel={() => setDialog((d) => (d?.kind === 'resetWarn' ? null : d))}
          onConfirm={() => setDialog({ kind: 'resetType' })}
        />
      )}

      {dialog?.kind === 'resetType' && (
        <TextPromptModal
          title="리셋 확인"
          label="확인을 위해 RESET 을 대문자로 입력"
          placeholder="RESET"
          submitLabel="리셋"
          onClose={() => setDialog(null)}
          onSubmit={async (value) => {
            await resetIngestedAll(value);
          }}
        />
      )}

      {dialog?.kind === 'removeChat' && (
        <ConfirmModal
          title="채팅방 연동 삭제"
          message={`'${dialog.integration.label}' 연동을 삭제합니다. 이 방의 폴링과 자동 공지 변환이 즉시 멈추고, 되돌릴 수 없습니다.`}
          confirmLabel="삭제"
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            await remove(dialog.integration);
          }}
        />
      )}

      {dialog?.kind === 'removeCalendar' && (
        <ConfirmModal
          title="회의실 연동 삭제"
          message={`'${dialog.integration.label}' 회의실 연동을 삭제합니다. 일정 캐시가 멈추고, 되돌릴 수 없습니다.`}
          confirmLabel="삭제"
          onCancel={() => setDialog(null)}
          onConfirm={async () => {
            await removeCalendar(dialog.integration);
          }}
        />
      )}

      {probeNotice && (
        <ConfirmModal
          title="Callback Probe 발송 완료"
          message={probeNotice}
          confirmLabel="확인"
          danger={false}
          hideCancel
          onCancel={() => setProbeNotice(null)}
          onConfirm={() => setProbeNotice(null)}
        />
      )}

      {me && hasCalendarScope && (
        <>
          <section style={section}>
            <h2 style={h2}>4. Calendar 회의실 추가</h2>
            <p style={dim}>
              Google Calendar의 회의실(Resource) 캘린더를 등록합니다. 60초마다
              현재 시각 ±12시간 범위 일정을 캐시합니다.
            </p>
            {roomDiscoveryErr && (
              <div style={errorStyle}>
                회의실 목록 불러오기 실패: {roomDiscoveryErr}
              </div>
            )}
            <form
              onSubmit={addCalendar}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 1fr) 240px 88px',
                gap: 10,
                maxWidth: 1200,
                marginTop: 14,
              }}
            >
              {availableRooms.length > 0 ? (
                <select
                  value={draftCalendarId}
                  onChange={(e) => {
                    setDraftCalendarId(e.target.value);
                    const found = availableRooms.find(
                      (room) => room.id === e.target.value,
                    );
                    if (found && !draftCalendarLabel) {
                      // Strip the common HNINE-10F- prefix when present so the
                      // operator gets a clean default like "Hanoi".
                      const cleaned = found.summary
                        .replace(/^HNINE-\d+F-/, '')
                        .trim();
                      setDraftCalendarLabel(cleaned);
                    }
                  }}
                  style={input}
                >
                  <option value="">
                    내 회의실 캘린더에서 선택 ({availableRooms.length}개)
                  </option>
                  {availableRooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.isResource ? '🏢 ' : ''}
                      {room.summary} — {room.id}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder="캘린더 ID (예: hnine.com_xxx@resource.calendar.google.com)"
                  value={draftCalendarId}
                  onChange={(e) => setDraftCalendarId(e.target.value)}
                  style={input}
                />
              )}
              <input
                type="text"
                placeholder="라벨 (예: Hanoi)"
                value={draftCalendarLabel}
                onChange={(e) => setDraftCalendarLabel(e.target.value)}
                style={input}
              />
              <button type="submit" style={primaryButton}>
                추가
              </button>
            </form>
          </section>

          <section style={section}>
            <h2 style={h2}>5. 등록된 회의실 ({calendars.length})</h2>
            <div style={{ display: 'grid', gap: 10, maxWidth: 1200 }}>
              {calendars.map((it) => (
                <div key={it.id} style={row}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ fontWeight: 600 }}>{it.label}</div>
                    <div
                      style={{
                        color: 'var(--color-text-dim)',
                        fontSize: 13,
                        fontFamily: 'monospace',
                      }}
                    >
                      {it.calendarId}
                    </div>
                  </div>
                  <div style={{ color: 'var(--color-text-dim)', fontSize: 13 }}>
                    <div>일정 {it.eventCount}건</div>
                    <div>
                      {it.lastPolledAt
                        ? `폴링 ${relativeTime(it.lastPolledAt)}`
                        : '미폴링'}
                    </div>
                  </div>
                  <div
                    style={{
                      color: it.lastError ? 'var(--color-red)' : 'var(--color-text-dim)',
                      fontSize: 13,
                    }}
                  >
                    {it.lastError ?? '정상'}
                  </div>
                  <div style={actionGroup}>
                    <button
                      onClick={() => void pollCalendarNow(it)}
                      style={secondaryButton}
                      title="지금 폴링"
                    >
                      폴링
                    </button>
                    <button
                      onClick={() => void toggleCalendar(it)}
                      style={{
                        ...secondaryButton,
                        color: it.enabled ? 'var(--color-warning)' : 'var(--color-green)',
                      }}
                    >
                      {it.enabled ? '비활성' : '활성'}
                    </button>
                    <button
                      onClick={() => setDialog({ kind: 'removeCalendar', integration: it })}
                      style={secondaryButton}
                    >
                      삭제
                    </button>
                  </div>
                </div>
              ))}
              {calendars.length === 0 && (
                <div style={dim}>아직 등록된 회의실이 없습니다.</div>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Phase 6.9 — "공지 목록" tab. Lists every ingested chat notice with its
// confirmation status and per-row activate / deactivate / delete controls.
// Reads/writes the new /chat/notices endpoints. Status gating means only
// 'confirmed' rows reach the Player rotation pool, so this table is the single
// place an operator controls what is actually broadcast.
// =============================================================================


function PollPreviewModal({
  preview,
  onClose,
  onReparse,
  onLocalMessageToggle,
}: {
  preview: PollPreview;
  onClose: () => void;
  /** Provided only for Chat previews. Called with `onlyMissing` flag. */
  onReparse: ((onlyMissing: boolean) => void) | undefined;
  /** Provided only for Chat previews. Local optimistic toggle that mutates
   *  the parent's `preview.messages` so the UI reflects the new excluded
   *  state immediately without a refetch. */
  onLocalMessageToggle: (messageId: string, excluded: boolean) => void;
}): JSX.Element {
  const isReparsing =
    preview.kind === 'chat' && preview.pollingState === 'reparsing';
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 29,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-line)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 1080,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          color: 'var(--color-text)',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '19px 24px',
            borderBottom: '1px solid var(--color-line)',
          }}
        >
          <div>
            <div style={{ fontSize: 19, fontWeight: 600 }}>
              {preview.kind === 'chat' ? '💬' : '📅'} {preview.label} — 폴링 결과
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-dim)', marginTop: 5 }}>
              {preview.pollingState === 'polling' && '폴링 중...'}
              {preview.pollingState === 'reparsing' &&
                'LLM 재파싱 중... (최대 30초)'}
              {preview.pollingState === 'loaded' &&
                (preview.kind === 'chat'
                  ? `최근 ${preview.messages.length}건의 처리된 메시지`
                  : `±12시간 범위 ${preview.events.length}건의 일정`)}
              {preview.pollingState === 'error' && '오류 발생'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {onReparse && preview.kind === 'chat' && (
              <>
                <button
                  onClick={() => onReparse(true)}
                  disabled={isReparsing}
                  style={{
                    ...modalCloseButton,
                    borderColor: 'var(--color-blue)',
                    color: 'var(--color-blue)',
                    opacity: isReparsing ? 0.5 : 1,
                    cursor: isReparsing ? 'wait' : 'pointer',
                  }}
                  title="파싱 결과가 없는 최근 메시지 10건만 LLM에 다시 보내기"
                >
                  미파싱 재처리
                </button>
                <button
                  onClick={() => onReparse(false)}
                  disabled={isReparsing}
                  style={{
                    ...modalCloseButton,
                    borderColor: 'var(--color-blue)',
                    color: 'var(--color-blue)',
                    opacity: isReparsing ? 0.5 : 1,
                    cursor: isReparsing ? 'wait' : 'pointer',
                  }}
                  title="최근 메시지 10건 강제 재파싱 (이미 파싱된 것도 덮어씀)"
                >
                  전체 재파싱
                </button>
              </>
            )}
            <button onClick={onClose} style={modalCloseButton}>
              닫기 ✕
            </button>
          </div>
        </header>
        {preview.kind === 'chat' && preview.reparseStats && (
          <div
            style={{
              padding: '10px 24px',
              fontSize: 14,
              color: 'var(--color-green)',
              background: 'var(--color-green-soft)',
              borderBottom: '1px solid var(--color-green)',
            }}
          >
            재파싱 완료 — 대상 {preview.reparseStats.total}건 / 성공{' '}
            {preview.reparseStats.succeeded}건 / 실패{' '}
            {preview.reparseStats.failed}건
            {preview.reparseStats.hydrated ? ` / 본문 복구 ${preview.reparseStats.hydrated}건` : ''}
            {preview.reparseStats.skippedMissingBody
              ? ` / 본문 없음 ${preview.reparseStats.skippedMissingBody}건`
              : ''}
          </div>
        )}
        <div
          style={{
            padding: 24,
            overflow: 'auto',
            flex: 1,
          }}
        >
          {preview.pollingState === 'polling' && (
            <div style={{ color: 'var(--color-text-dim)', textAlign: 'center', padding: 48 }}>
              폴링 중...
            </div>
          )}
          {preview.pollingState === 'reparsing' && (
            <div style={{ color: 'var(--color-blue)', textAlign: 'center', padding: 48 }}>
              LLM 재파싱 중... 각 메시지당 약 3초 (최대 30초)
              <br />
              <span style={{ fontSize: 13, color: 'var(--color-blue)' }}>
                Gemini/LLM이 본문을 [HEADER] / [SUBTITLE] / [META DATA] /
                [CONTENT] 로 정리하고 있습니다.
              </span>
            </div>
          )}
          {preview.pollingState === 'error' && (
            <div style={modalErrorBox}>
              {preview.errorMessage ?? '알 수 없는 오류'}
            </div>
          )}
          {preview.pollingState === 'loaded' && preview.kind === 'chat' && (
            <ChatPreviewList
              integrationId={preview.integrationId}
              messages={preview.messages}
              onLocalToggle={onLocalMessageToggle}
            />
          )}
          {preview.pollingState === 'loaded' && preview.kind === 'calendar' && (
            <CalendarPreviewList
              events={preview.events}
              rangeFromIso={preview.rangeFromIso}
              rangeToIso={preview.rangeToIso}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
}

// =============================================================================
// CsvPromptModal — "쉼표로 구분한 목록"을 고치는 모달(승인방 / 운영자).
//
// 공용 TextPromptModal을 쓰지 않는 이유는 하나다: 그쪽은 빈 값이면 확인 버튼이
// 꺼진다. 그런데 승인방·운영자는 '비워 두면 그 기능을 끈다'가 의도된 설정값이라
// 빈 값 저장을 막으면 기존 window.prompt로 되던 일이 안 된다. 나머지(ModalShell의
// 고정 3분할·포커스 트랩·실패 사유 인라인 표시)는 공용 규칙 그대로다.
// =============================================================================
function CsvPromptModal({
  title,
  label,
  hint,
  initialValue,
  placeholder,
  onClose,
  onSubmit,
}: {
  title: string;
  label: string;
  /** 무엇을 비우면 무슨 일이 생기는지 알려주는 보조 설명. */
  hint: string;
  initialValue: string;
  placeholder?: string;
  onClose: () => void;
  onSubmit: (values: string[]) => void | Promise<void>;
}): JSX.Element {
  const [value, setValue] = useState(initialValue);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(splitCsv(value));
      onClose();
    } catch (e) {
      setError(describeApiError(e));
      setBusy(false);
    }
  };
  return (
    <ModalShell
      title={title}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            취소
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <label className="block">
        <span className="block font-mono text-[12px] font-medium uppercase tracking-monoWide text-text-dim mb-1.5">
          {label}
        </span>
        <input
          autoFocus
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void submit();
          }}
          className="w-full px-2.5 py-2 bg-panel-2 border border-line rounded text-[15px] text-text focus:outline-none focus:border-accent"
        />
      </label>
      <p className="text-[13px] text-text-dim leading-relaxed">{hint}</p>
      {error && <div className="font-mono text-[12px] text-red">{error}</div>}
    </ModalShell>
  );
}

// 공지 키워드 ↔ filterPattern(정규식) 변환.
// 표준 형태: ^\[(키워드1|키워드2)\]\s*  (대괄호 prefix 매칭)
const NOTICE_KEYWORD_PATTERN = /^\^\\\[\((.+)\)\\\]\\s\*$/;

function escapeRegexLiteral(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function keywordsToPattern(keywords: string[]): string {
  const escaped = keywords
    .map((k) => escapeRegexLiteral(k.trim()))
    .filter((k) => k.length > 0);
  return `^\\[(${escaped.join('|')})\\]\\s*`;
}

/** 표준 형태면 키워드 배열, 아니면 null(커스텀 정규식 → 원시 편집). */
function patternToKeywords(pattern: string): string[] | null {
  const m = NOTICE_KEYWORD_PATTERN.exec(pattern);
  const inner = m?.[1];
  if (!inner) return null;
  return inner.split('|').map((part) => part.replace(/\\(.)/g, '$1'));
}

function ChatPreviewList({
  integrationId,
  messages,
  onLocalToggle,
}: {
  integrationId: string;
  messages: ChatRecentMessage[];
  onLocalToggle: (messageId: string, excluded: boolean) => void;
}): JSX.Element {
  const toggleExcluded = async (m: ChatRecentMessage) => {
    const next = !m.excluded;
    // Optimistic — flip locally, revert on error.
    onLocalToggle(m.messageId, next);
    try {
      const r = await fetch(
        `${API_BASE}/api/integrations/chat/${integrationId}/message`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messageId: m.messageId, excluded: next }),
        },
      );
      if (!r.ok) throw new Error(`${r.status}`);
    } catch (e) {
      onLocalToggle(m.messageId, !next);
      toast(`제외 토글 실패: ${describeApiError(e)}`, 'error');
    }
  };

  if (messages.length === 0) {
    return (
      <div style={{ color: 'var(--color-text-dim)', textAlign: 'center', padding: 48 }}>
        아직 처리된 메시지가 없습니다. 필터 패턴(<code style={inlineCode}>^\[(공지|공지사항)\]</code>)에 맞는 새 메시지가 채팅방에 올라오면 여기에 표시됩니다.
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {messages.map((m) => (
        <div
          key={m.messageId}
          style={{
            ...previewRowBox,
            opacity: m.excluded ? 0.45 : 1,
            borderColor: m.excluded ? 'var(--color-red)' : (previewRowBox as { borderColor?: string }).borderColor,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 13,
              color: 'var(--color-text-dim)',
              marginBottom: 7,
            }}
          >
            <span>
              {m.senderName ?? '알 수 없음'}
              {m.messageCreateTime && (
                <> · {new Date(m.messageCreateTime).toLocaleString('ko-KR')}</>
              )}
              {m.parsed && <StatusBadge status={m.parsed.status} />}
              {m.excluded && (
                <span
                  style={{
                    marginLeft: 10,
                    padding: '1px 7px',
                    borderRadius: 5,
                    background: 'var(--color-red-soft)',
                    border: '1px solid var(--color-red)',
                    color: 'var(--color-red)',
                    fontSize: 12,
                  }}
                >
                  제외됨
                </span>
              )}
            </span>
            <span style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {m.noticeName && (
                <span style={{ color: 'var(--color-green)' }}>
                  → Scene: {m.noticeName}
                </span>
              )}
              <button
                type="button"
                onClick={() => void toggleExcluded(m)}
                style={{
                  ...secondaryButton,
                  padding: '2px 10px',
                  fontSize: 13,
                  ...(m.excluded
                    ? { borderColor: 'var(--color-green)', color: 'var(--color-green)' }
                    : { borderColor: 'var(--color-red)', color: 'var(--color-red)' }),
                }}
                title={
                  m.excluded
                    ? '회전 풀에서 다시 포함시키기'
                    : '회전 풀에서 제외하기 (이 공지 숨김)'
                }
              >
                {m.excluded ? '↺ 포함' : '✕ 제외'}
              </button>
            </span>
          </div>
          {m.parsed && <ParsedNoticeBlock parsed={m.parsed} />}
          <details style={{ marginTop: m.parsed ? 8 : 0 }}>
            <summary
              style={{
                fontSize: 13,
                color: 'var(--color-text-dim)',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              원본 메시지 보기
            </summary>
            <div
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: 14,
                lineHeight: 1.5,
                color: 'var(--color-text)',
                marginTop: 7,
                padding: '7px 12px',
                background: 'var(--color-panel-3)',
                borderRadius: 5,
                border: '1px dashed var(--color-line)',
              }}
            >
              {m.messageText || (
                <span style={{ color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
                  (본문 없음 — 0.2.0 이전 ingest 행)
                </span>
              )}
            </div>
          </details>
        </div>
      ))}
    </div>
  );
}

function StatusBadge({
  status,
}: {
  status: 'upcoming' | 'ongoing' | 'expired' | 'unknown';
}): JSX.Element {
  const map = {
    upcoming: {
      label: '예정',
      bg: 'var(--color-blue-soft)',
      border: 'var(--color-blue)',
      color: 'var(--color-blue)',
    },
    ongoing: {
      label: '진행중',
      bg: 'var(--color-green-soft)',
      border: 'var(--color-green)',
      color: 'var(--color-green)',
    },
    expired: {
      label: '지난 공지',
      bg: 'var(--color-red-soft)',
      border: 'var(--color-red)',
      color: 'var(--color-red)',
    },
    unknown: {
      label: '시점 없음',
      bg: 'var(--color-panel-2)',
      border: 'var(--color-line-strong)',
      color: 'var(--color-text-dim)',
    },
  } as const;
  const m = map[status];
  return (
    <span
      style={{
        marginLeft: 12,
        padding: '2px 10px',
        borderRadius: 999,
        background: m.bg,
        border: `1px solid ${m.border}`,
        color: m.color,
        fontSize: 12,
        fontWeight: 600,
      }}
    >
      {m.label}
    </span>
  );
}

function ParsedNoticeBlock({
  parsed,
}: {
  parsed: NonNullable<ChatRecentMessage['parsed']>;
}): JSX.Element {
  const hasMeta =
    parsed.metaData.date || parsed.metaData.time || parsed.metaData.location;
  return (
    <div
      style={{
        background: 'var(--color-panel-2)',
        border: '1px solid var(--color-line)',
        borderLeft: '3px solid var(--color-blue)',
        borderRadius: 5,
        padding: '14px 17px',
        fontSize: 14,
        lineHeight: 1.55,
        color: 'var(--color-text)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <ParsedSection label="HEADER">
        <span style={{ fontWeight: 600, fontSize: 16 }}>
          {parsed.header || '—'}
        </span>
      </ParsedSection>
      <ParsedSection label="SUBTITLE">
        <span>{parsed.subtitle || '—'}</span>
      </ParsedSection>
      {hasMeta && (
        <>
          <ParsedDivider />
          <ParsedSection label="META DATA">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr',
                rowGap: 4,
                columnGap: 12,
                fontSize: 14,
              }}
            >
              {parsed.metaData.date && (
                <>
                  <span style={metaKey}>날짜</span>
                  <span>{parsed.metaData.date}</span>
                </>
              )}
              {parsed.metaData.time && (
                <>
                  <span style={metaKey}>시간</span>
                  <span>{parsed.metaData.time}</span>
                </>
              )}
              {parsed.metaData.location && (
                <>
                  <span style={metaKey}>장소</span>
                  <span>{parsed.metaData.location}</span>
                </>
              )}
            </div>
          </ParsedSection>
        </>
      )}
      <ParsedDivider />
      <ParsedSection label="CONTENT">
        <div
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'var(--color-text)',
          }}
        >
          {parsed.content || '—'}
        </div>
      </ParsedSection>
      {(parsed.eventStartDate || parsed.eventEndDate) && (
        <div
          style={{
            fontSize: 12,
            fontFamily: 'monospace',
            color: 'var(--color-blue)',
            marginTop: 5,
            opacity: 0.7,
          }}
          title="코드가 이 ISO 범위로 status를 계산합니다"
        >
          ISO range: {parsed.eventStartDate ?? '?'} ~{' '}
          {parsed.eventEndDate ?? '?'}
        </div>
      )}
    </div>
  );
}

function ParsedSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <div style={parsedLabelLine}>[{label}]</div>
      {children}
    </div>
  );
}

function ParsedDivider(): JSX.Element {
  return (
    <div
      style={{
        height: 1,
        background: 'linear-gradient(to right, var(--color-line) 0%, var(--color-line) 100%)',
        opacity: 0.6,
      }}
    />
  );
}

const parsedLabelLine: React.CSSProperties = {
  color: 'var(--color-blue)',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.05em',
  marginBottom: 4,
};

const metaKey: React.CSSProperties = {
  color: 'var(--color-text-dim)',
  fontWeight: 500,
};

function CalendarPreviewList({
  events,
  rangeFromIso,
  rangeToIso,
}: {
  events: CalendarEvent[];
  rangeFromIso: string;
  rangeToIso: string;
}): JSX.Element {
  if (events.length === 0) {
    return (
      <div style={{ color: 'var(--color-text-dim)', textAlign: 'center', padding: 48 }}>
        ±12시간 범위에 일정이 없습니다.
        <div style={{ fontSize: 13, marginTop: 10, fontFamily: 'monospace' }}>
          {rangeFromIso}
          <br />~ {rangeToIso}
        </div>
      </div>
    );
  }
  const now = Date.now();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {events.map((e) => {
        const isPast = e.endAt < now;
        const isCurrent = e.startAt <= now && e.endAt >= now;
        const accent = isCurrent ? 'var(--color-accent)' : isPast ? 'var(--color-line-strong)' : 'var(--color-blue)';
        return (
          <div
            key={e.id}
            style={{
              ...previewRowBox,
              borderLeft: `4px solid ${accent}`,
              opacity: isPast ? 0.55 : 1,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 14,
                marginBottom: 5,
              }}
            >
              <span style={{ fontSize: 17, fontWeight: 600 }}>
                {e.summary || (
                  <span style={{ color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
                    (제목 없음)
                  </span>
                )}
                {isCurrent && (
                  <span
                    style={{
                      marginLeft: 10,
                      padding: '1px 10px',
                      borderRadius: 999,
                      background: 'var(--color-accent)',
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    NOW
                  </span>
                )}
                {e.status !== 'confirmed' && (
                  <span
                    style={{
                      marginLeft: 10,
                      color: 'var(--color-warning)',
                      fontSize: 13,
                    }}
                  >
                    [{e.status}]
                  </span>
                )}
              </span>
              <span style={{ color: 'var(--color-text-dim)', fontSize: 14, whiteSpace: 'nowrap' }}>
                {formatTimeRange(e.startAt, e.endAt)}
              </span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--color-text-dim)' }}>
              {e.organizerEmail ?? '주최자 미확인'} · {e.startIso}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatTimeRange(startMs: number, endMs: number): string {
  const fmt = new Intl.DateTimeFormat('ko-KR', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const start = fmt.format(new Date(startMs));
  const endTimeOnly = new Intl.DateTimeFormat('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(endMs));
  return `${start} ~ ${endTimeOnly}`;
}

const inlineCode: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 13,
  background: 'var(--color-panel-3)',
  color: 'var(--color-text)',
  padding: '1px 5px',
  borderRadius: 4,
};
const previewRowBox: React.CSSProperties = {
  background: 'var(--color-panel-2)',
  border: '1px solid var(--color-line)',
  borderRadius: 7,
  padding: '12px 17px',
};
const modalCloseButton: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--color-line-strong)',
  color: 'var(--color-text)',
  borderRadius: 7,
  padding: '7px 14px',
  fontSize: 14,
  cursor: 'pointer',
};
const modalErrorBox: React.CSSProperties = {
  background: 'var(--color-red-soft)',
  border: '1px solid var(--color-red)',
  color: 'var(--color-red)',
  padding: '14px 19px',
  borderRadius: 7,
  fontSize: 16,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

function ScopeBadge({
  label,
  granted,
}: {
  label: string;
  granted: boolean;
}): JSX.Element {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '5px 12px',
        borderRadius: 999,
        fontSize: 13,
        border: '1px solid',
        borderColor: granted ? 'var(--color-green)' : 'var(--color-line-strong)',
        color: granted ? 'var(--color-green)' : 'var(--color-text-dim)',
        background: granted ? 'var(--color-green-soft)' : 'var(--color-panel-2)',
      }}
    >
      {granted ? '✓' : '✕'} {label}
    </span>
  );
}

const section: React.CSSProperties = { marginBottom: 38 };
const h2: React.CSSProperties = { fontSize: 19, marginBottom: 14 };
const dim: React.CSSProperties = { color: 'var(--color-text-dim)', fontSize: 16 };
const codeStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 14,
  background: 'var(--color-panel-3)',
  color: 'var(--color-text)',
  padding: '1px 7px',
  borderRadius: 4,
};
const input: React.CSSProperties = {
  background: 'var(--color-panel)',
  color: 'var(--color-text)',
  border: '1px solid var(--color-line)',
  borderRadius: 7,
  padding: '10px 14px',
  fontSize: 16,
  minWidth: 0,
  boxSizing: 'border-box',
};
const primaryButton: React.CSSProperties = {
  background: 'var(--color-accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 7,
  padding: '10px 19px',
  fontSize: 16,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 106,
  whiteSpace: 'nowrap',
};
const secondaryButton: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--color-line-strong)',
  color: 'var(--color-text)',
  borderRadius: 7,
  padding: '7px 12px',
  fontSize: 14,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
};
const dismissButton: React.CSSProperties = {
  ...secondaryButton,
  marginLeft: 14,
  padding: '2px 10px',
  fontSize: 13,
};
const errorStyle: React.CSSProperties = {
  background: 'var(--color-red-soft)',
  border: '1px solid var(--color-red)',
  color: 'var(--color-red)',
  padding: '10px 14px',
  borderRadius: 7,
  marginBottom: 19,
  fontSize: 16,
};
const row: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '2fr 1fr 1.5fr auto',
  gap: 19,
  padding: 14,
  background: 'var(--color-panel-2)',
  border: '1px solid var(--color-line)',
  borderRadius: 10,
  alignItems: 'center',
  fontSize: 16,
};
const actionGroup: React.CSSProperties = {
  display: 'flex',
  gap: 7,
  justifyContent: 'flex-end',
  alignItems: 'center',
  flexWrap: 'nowrap',
};

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}초 전`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  return `${Math.floor(hr / 24)}일 전`;
}
